import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { setCrewOverrides, setLastRecommendedCrew } from "../features/shipBuilderSlice";
import { selectMinimumCrew } from "../selectors/shipBuilderSelectors";
import { CrewRole } from "../../types/crew";

/**
 * Middleware to automatically update crew assignments when crew requirements change
 * This middleware watches for changes that affect crew requirements and automatically
 * updates crew overrides to match the new requirements.
 * 
 * This ensures that when passengers are added (which increases steward requirements),
 * the crew assignments are automatically updated to reflect the new requirements.
 */
export const crewAutoAssignMiddleware: Middleware<{}, RootState> = (store) => (next) => (action: any) => {
  // First, let the action pass through
  const result = next(action);

  // Actions that should trigger crew requirement recalculation
  const triggerActions = [
    // Passenger changes that affect steward requirements
    "shipBuilder/setPassengerCount",
    "shipBuilder/setPassengerCargoOverride",
    
    // Ship configuration changes that affect crew requirements
    "shipBuilder/setShipSize", // Ship size affects crew requirements
    "shipBuilder/setDrives", // Drives affect engineer requirements
    "shipBuilder/setPowerPlant", // Power plant affects engineer requirements
    "shipBuilder/setWeapons", // Weapons affect gunner requirements
    "shipBuilder/addWeapon", // Adding weapons affects gunner requirements
    "shipBuilder/removeWeapon", // Removing weapons affects gunner requirements
    "shipBuilder/addWeaponMount", // Adding weapon mounts affects gunner requirements
    "shipBuilder/removeWeaponMount", // Removing weapon mounts affects gunner requirements
    "shipBuilder/setSmallCraft", // Small craft affect pilot/engineer requirements
  ];

  if (triggerActions.includes(action.type)) {
    // Get the current state after the action
    const state = store.getState();
    
    // Get the new recommendations and current overrides
    const newRecommended = selectMinimumCrew(state) as Record<string, number>;
    const currentOverrides = state.shipBuilder.crewOverrides || {};
    const lastRecommended = state.shipBuilder.lastRecommendedCrewByRole || {};

    let changed = false;
    const nextOverrides: Record<string, number> = { ...currentOverrides };

    Object.entries(newRecommended).forEach(([roleKey, required]) => {
      const role = Object.values(CrewRole).find((r) => r === roleKey) as CrewRole;
      if (!role) return;

      const prevRecommended = typeof lastRecommended[roleKey] === 'number' ? lastRecommended[roleKey] : required;
      const prevAssignedVal = typeof currentOverrides[role] === 'number' ? currentOverrides[role] as number : prevRecommended;

      const userExtra = Math.max(0, prevAssignedVal - prevRecommended);
      const newAssigned = Math.max(0, required + userExtra);

      if (newAssigned !== prevAssignedVal) {
        nextOverrides[role] = newAssigned as number;
        changed = true;
      }
    });

    if (changed) {
      store.dispatch(setCrewOverrides(nextOverrides));
    }

    // Always update the last recommended snapshot to the latest
    store.dispatch(setLastRecommendedCrew(newRecommended));
  }

  return result;
};

export default crewAutoAssignMiddleware;
